const API_BASE = process.env.REACT_APP_API_URL || '/api';

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* Safari private / in-app browser */
  }
}

let authToken = readStorage('hrd_token') || null;

export function setAuthToken(token) {
  authToken = token;
  writeStorage('hrd_token', token);
}

export function getAuthToken() {
  return authToken || readStorage('hrd_token');
}

const DEFAULT_FETCH_TIMEOUT_MS = 12000;

function withTimeoutSignal(existingSignal, timeoutMs) {
  if (!timeoutMs || timeoutMs <= 0) {
    return { signal: existingSignal, cancel: () => {} };
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  if (existingSignal) {
    if (existingSignal.aborted) controller.abort();
    else existingSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return {
    signal: controller.signal,
    cancel: () => clearTimeout(timer),
  };
}

export async function apiFetch(path, options = {}) {
  const { timeoutMs = DEFAULT_FETCH_TIMEOUT_MS, signal, headers: extraHeaders, ...rest } = options;
  const headers = {
    'Content-Type': 'application/json',
    ...(extraHeaders || {}),
  };

  const token = getAuthToken();

  if (token) headers.Authorization = `Bearer ${token}`;

  const timed = withTimeoutSignal(signal, timeoutMs);
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...rest,
      headers,
      signal: timed.signal,
    });
  } catch (error) {
    timed.cancel();
    if (error?.name === 'AbortError') {
      const err = new Error('Koneksi terlalu lama. Periksa jaringan Anda, lalu coba lagi.');
      err.status = 408;
      throw err;
    }
    throw error;
  }
  timed.cancel();

  if (res.status === 401) {
    setAuthToken(null);
    window.dispatchEvent(new Event('auth:logout'));
  }

  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();

  if (!contentType.includes('application/json')) {
    const err = new Error('API tidak merespons JSON. Pastikan backend /api sudah aktif dan URL API production sudah benar.');
    err.status = res.status;
    err.data = data;
    throw err;
  }

  if (!res.ok) {
    const err = new Error(data?.error || data?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

export function apiGet(path) {
  return apiFetch(path, { method: 'GET' });
}

export function apiPost(path, body, options = {}) {
  return apiFetch(path, { method: 'POST', body: JSON.stringify(body), ...options });
}

export function apiPut(path, body) {
  return apiFetch(path, { method: 'PUT', body: JSON.stringify(body) });
}

export function apiDelete(path) {
  return apiFetch(path, { method: 'DELETE' });
}

export async function apiDownload(path) {
  const token = getAuthToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (res.status === 401) {
    setAuthToken(null);
    window.dispatchEvent(new Event('auth:logout'));
  }

  if (!res.ok) {
    const contentType = res.headers.get('content-type') || '';
    let message = `HTTP ${res.status}`;
    if (contentType.includes('application/json')) {
      const data = await res.json().catch(() => ({}));
      message = data?.error || data?.message || message;
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const disp = res.headers.get('content-disposition') || '';
  const match = disp.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename = match ? decodeURIComponent(match[1]) : 'backup.sql.gz';
  return { blob, filename };
}

/** Polling substitute for Firestore onSnapshot (visibility-aware) */
export function createPollingListener(fetchFn, callback, intervalMs = 3000) {
  let active = true;
  let timer = null;
  let inFlight = false;

  const delay = () => {
    if (typeof document !== 'undefined' && document.hidden) return Math.max(intervalMs * 4, 15000);
    return intervalMs;
  };

  const poll = async () => {
    if (!active || inFlight) return;
    inFlight = true;
    try {
      const data = await fetchFn();
      if (active) callback(data);
    } catch (e) {
      console.error('Polling error:', e);
    } finally {
      inFlight = false;
      if (active) timer = setTimeout(poll, delay());
    }
  };

  const onVisible = () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    if (timer) clearTimeout(timer);
    poll();
  };

  poll();

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
  }

  return () => {
    active = false;
    if (timer) clearTimeout(timer);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    }
  };
}

export async function apiUploadForm(path, formData) {
  const token = getAuthToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (res.status === 401) {
    setAuthToken(null);
    window.dispatchEvent(new Event('auth:logout'));
  }

  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const message = (data && data.error) || (data && data.message) || `HTTP ${res.status}`;
    throw new Error(typeof message === 'string' ? message : 'Upload gagal.');
  }

  return data;
}

export async function uploadFile(category, file) {
  const formData = new FormData();
  formData.append('file', file);

  const token = getAuthToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/files/${category}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || 'Upload failed');
  return data;
}
